import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  ExternalCalendarEvent,
  MyAbsence,
  MyAbsencePayload,
  PlannableUser,
  PlanningProfilePayload,
  ResourceAssignment,
  ResourceAssignmentPayload,
  ResourceCategoryDef,
  ResourceSkill
} from '../Models/resource-assignment.model';

@Injectable({ providedIn: 'root' })
export class ResourcePlanningService {
  private apiUrl = environment.apiBaseUrl + '/resourceassignment';
  private planUrl = environment.apiBaseUrl + '/resourceplanning';

  constructor(private http: HttpClient) { }

  // ---- Benutzer / Profile ----
  getPlannableUsers(): Observable<PlannableUser[]> {
    const params = new HttpParams().set('onlyPlannable', true);
    return this.http.get<PlannableUser[]>(`${this.planUrl}/users`, { params });
  }

  getAllUsers(): Observable<PlannableUser[]> {
    const params = new HttpParams().set('onlyPlannable', false);
    return this.http.get<PlannableUser[]>(`${this.planUrl}/users`, { params });
  }

  setUserProfile(userId: string, payload: PlanningProfilePayload): Observable<void> {
    return this.http.put<void>(`${this.planUrl}/users/${userId}`, payload);
  }

  // ---- Skills ----
  getSkills(): Observable<ResourceSkill[]> {
    return this.http.get<ResourceSkill[]>(`${this.planUrl}/skills`);
  }

  createSkill(skill: Partial<ResourceSkill>): Observable<ResourceSkill> {
    return this.http.post<ResourceSkill>(`${this.planUrl}/skills`, skill);
  }

  updateSkill(id: number, skill: Partial<ResourceSkill>): Observable<void> {
    return this.http.put<void>(`${this.planUrl}/skills/${id}`, skill);
  }

  deleteSkill(id: number): Observable<void> {
    return this.http.delete<void>(`${this.planUrl}/skills/${id}`);
  }

  // ---- Kategorien ----
  getCategories(): Observable<ResourceCategoryDef[]> {
    return this.http.get<ResourceCategoryDef[]>(`${this.planUrl}/categories`);
  }

  createCategory(cat: Partial<ResourceCategoryDef>): Observable<ResourceCategoryDef> {
    return this.http.post<ResourceCategoryDef>(`${this.planUrl}/categories`, cat);
  }

  updateCategory(id: number, cat: Partial<ResourceCategoryDef>): Observable<void> {
    return this.http.put<void>(`${this.planUrl}/categories/${id}`, cat);
  }

  deleteCategory(id: number): Observable<void> {
    return this.http.delete<void>(`${this.planUrl}/categories/${id}`);
  }

  // ---- Einplanungen ----
  getAssignments(from?: Date, to?: Date): Observable<ResourceAssignment[]> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', this.toLocalIso(from));
    }
    if (to) {
      params = params.set('to', this.toLocalIso(to));
    }
    return this.http.get<ResourceAssignment[]>(this.apiUrl, { params });
  }

  createAssignment(payload: ResourceAssignmentPayload): Observable<ResourceAssignment> {
    return this.http.post<ResourceAssignment>(this.apiUrl, payload);
  }

  updateAssignment(id: number, payload: ResourceAssignmentPayload): Observable<ResourceAssignment> {
    return this.http.put<ResourceAssignment>(`${this.apiUrl}/${id}`, payload);
  }

  deleteAssignment(id: number, sendCancellation: boolean): Observable<void> {
    const params = new HttpParams().set('sendCancellation', sendCancellation);
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { params });
  }

  deleteSeries(seriesId: string, sendCancellation: boolean): Observable<void> {
    const params = new HttpParams().set('sendCancellation', sendCancellation);
    return this.http.delete<void>(`${this.apiUrl}/series/${seriesId}`, { params });
  }

  sendInvite(id: number): Observable<ResourceAssignment> {
    return this.http.post<ResourceAssignment>(`${this.apiUrl}/${id}/invite`, {});
  }

  setStatus(id: number, status: string): Observable<ResourceAssignment> {
    return this.http.post<ResourceAssignment>(`${this.apiUrl}/${id}/status`, { status });
  }

  approveAbsence(id: number): Observable<ResourceAssignment> {
    return this.http.post<ResourceAssignment>(`${this.apiUrl}/${id}/approve`, {});
  }

  rejectAbsence(id: number): Observable<ResourceAssignment> {
    return this.http.post<ResourceAssignment>(`${this.apiUrl}/${id}/reject`, {});
  }

  // ---- Self-Service: eigene Abwesenheiten ----
  getMyAbsences(): Observable<MyAbsence[]> {
    return this.http.get<MyAbsence[]>(`${environment.apiBaseUrl}/myabsence`);
  }

  getMyAbsenceCategories(): Observable<ResourceCategoryDef[]> {
    return this.http.get<ResourceCategoryDef[]>(`${environment.apiBaseUrl}/myabsence/categories`);
  }

  requestMyAbsence(payload: MyAbsencePayload): Observable<MyAbsence> {
    return this.http.post<MyAbsence>(`${environment.apiBaseUrl}/myabsence`, payload);
  }

  cancelMyAbsence(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiBaseUrl}/myabsence/${id}`);
  }

  // ---- Externe Kalender (geteilte Feeds) ----
  getExternalEvents(from: Date, to: Date): Observable<ExternalCalendarEvent[]> {
    const params = new HttpParams()
      .set('from', this.toLocalIso(from))
      .set('to', this.toLocalIso(to));
    return this.http.get<ExternalCalendarEvent[]>(`${this.planUrl}/external-events`, { params });
  }

  hideExternalEvent(userId: string, uid: string, start: string): Observable<void> {
    return this.http.post<void>(`${this.planUrl}/external-events/hide`, { userId, uid, start });
  }

  unhideExternalEvent(userId: string, uid: string, start: string): Observable<void> {
    return this.http.post<void>(`${this.planUrl}/external-events/unhide`, { userId, uid, start });
  }

  getMyFeed(): Observable<{ url: string | null }> {
    return this.http.get<{ url: string | null }>(`${environment.apiBaseUrl}/mycalendarfeed`);
  }

  setMyFeed(url: string | null): Observable<void> {
    return this.http.put<void>(`${environment.apiBaseUrl}/mycalendarfeed`, { url });
  }

  // Lokale Wandzeit ohne Zeitzonen-Offset an das Backend geben.
  private toLocalIso(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  }
}
